import React, { createContext, useContext, useEffect } from 'react';
import { useOrganizationStore } from '@/stores/organizationStore';
import { useOrganizationData } from '@/hooks/useOrganizationData';
import { supabaseManager } from '@/services/supabaseManager';
import type { Organization } from '@/types';
import type { OrganizationMembership } from '@/types';

export interface OrganizationContextValue {
  /** Current organization (from TanStack Query, source of truth) */
  currentOrganization: Organization | null;
  /** Current membership for the active user */
  currentMembership: OrganizationMembership | null;
  /** Whether organization data is loading */
  isLoading: boolean;
  /** Whether the organization query has errored */
  isError: boolean;
  /** Whether the organization query has completed at least once */
  isFetched: boolean;
  /** Latest organization query error */
  error: Error | null;
  /** Update organization (invalidates query cache on success) */
  updateOrganization: (data: Partial<Organization>) => Promise<boolean>;
  /** Invalidate organization query (e.g. after external change) */
  invalidateOrganization: () => void;
  /** Whether an update is in progress */
  isUpdatingOrganization: boolean;
}

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

/**
 * Provides organization data from TanStack Query to the React tree.
 * Single source of truth for server state.
 *
 * Must wrap protected routes (inside QueryClientProvider).
 */
export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const organizationStore = useOrganizationStore();
  const slug = organizationStore.currentOrganizationSlug;

  // Sync slug from localStorage on mount (handles rehydration, first load)
  useEffect(() => {
    const currentSlug = useOrganizationStore.getState().currentOrganizationSlug;
    if (!currentSlug) {
      const storedSlug = supabaseManager.getCurrentOrganizationSlug();
      if (storedSlug) {
        useOrganizationStore.getState().setCurrentOrganizationSlug(storedSlug);
      }
    }
  }, []);

  const {
    organization,
    membership,
    isLoading: isQueryLoading,
    isError,
    isFetched,
    error,
    updateOrganization,
    invalidateOrganization,
    isUpdatingOrganization,
  } = useOrganizationData(slug);

  const value: OrganizationContextValue = {
    currentOrganization: organization,
    currentMembership: membership,
    isLoading: isQueryLoading,
    isError,
    isFetched,
    error: error instanceof Error ? error : null,
    updateOrganization,
    invalidateOrganization,
    isUpdatingOrganization,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

/**
 * Read-only access to organization data. Use for components that only need to display org info.
 * For mutations (updateOrganization, switchOrganization) and helpers (hasRole, formatAmount),
 * use useOrganization() instead.
 */
export const useOrganizationContext = (): OrganizationContextValue => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganizationContext must be used within OrganizationProvider');
  }
  return context;
};
